import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

// third-party
import { FormattedMessage, useIntl } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, ButtonBase, Chip, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';

// assets
import { ArrowDown2, Add, TickCircle } from 'iconsax-react';

// project-imports
import AccountAvatar from 'sections/developer/AccountAvatar';
import useAuth from 'hooks/useAuth';

// types
import { ThemeMode } from 'types/config';

// ==============================|| HEADER CONTENT - ACCOUNT SWITCHER ||============================== //
//
// The developer account the console works as. Picking another one reloads the developer pages (see
// AuthContext); "Create account" opens the same wizard a new user gets.

const AccountSwitcher = () => {
  const theme = useTheme();
  const intl = useIntl();
  const navigate = useNavigate();

  const { accounts, activeAccount, setActiveAccount } = useAuth();

  const anchorRef = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState<boolean>(false);

  const close = () => setOpen(false);

  const typeLabel = (type: string) => intl.formatMessage({ id: `acct.type.${type}` });

  if (accounts.length === 0) {
    return (
      <Chip
        clickable
        color="primary"
        variant="outlined"
        icon={<Add size={16} />}
        label={intl.formatMessage({ id: 'acct.switcher.create' })}
        onClick={() => navigate('/onboarding')}
        sx={{ ml: 0.75 }}
      />
    );
  }

  return (
    <Box sx={{ flexShrink: 0, ml: 0.75, minWidth: 0 }}>
      <ButtonBase
        ref={anchorRef}
        aria-label={intl.formatMessage({ id: 'acct.switcher.open' })}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        sx={{
          p: 0.5,
          borderRadius: 1,
          maxWidth: { xs: 140, sm: 280 },
          '&:hover': { bgcolor: theme.palette.mode === ThemeMode.DARK ? 'secondary.light' : 'secondary.lighter' }
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
          <AccountAvatar name={activeAccount?.name ?? ''} logo={activeAccount?.logo} size="xs" />
          <Typography variant="subtitle1" noWrap sx={{ minWidth: 0 }}>
            {activeAccount?.name}
          </Typography>
          {activeAccount && (
            <Chip
              size="small"
              variant="outlined"
              color={activeAccount.type === 'organization' ? 'primary' : 'default'}
              label={typeLabel(activeAccount.type)}
              sx={{ display: { xs: 'none', md: 'inline-flex' } }}
            />
          )}
          <ArrowDown2 size={14} />
        </Stack>
      </ButtonBase>

      <Menu
        anchorEl={anchorRef.current}
        open={open}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 280, maxWidth: 360, mt: 1 } } }}
      >
        <Typography variant="caption" color="textSecondary" sx={{ px: 2, py: 0.75, display: 'block' }}>
          <FormattedMessage id="acct.switcher.title" />
        </Typography>

        {accounts.map((account) => (
          <MenuItem
            key={account.id}
            selected={account.id === activeAccount?.id}
            onClick={() => {
              close();
              if (account.id !== activeAccount?.id) setActiveAccount(account.id);
            }}
          >
            <ListItemIcon>
              <AccountAvatar name={account.name} logo={account.logo} size="xs" />
            </ListItemIcon>
            <ListItemText
              primary={account.name}
              secondary={`${typeLabel(account.type)} · ${intl.formatMessage({ id: `acct.role.${account.role}` })}`}
              primaryTypographyProps={{ noWrap: true }}
              secondaryTypographyProps={{ noWrap: true }}
            />
            {account.id === activeAccount?.id && <TickCircle size={18} variant="Bold" color={theme.palette.primary.main} />}
          </MenuItem>
        ))}

        <Divider />

        <MenuItem
          onClick={() => {
            close();
            navigate('/onboarding');
          }}
        >
          <ListItemIcon>
            <Add size={18} />
          </ListItemIcon>
          <ListItemText primary={<FormattedMessage id="acct.switcher.create" />} />
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default AccountSwitcher;
